export interface NewsArticle {
  id: string;
  title: string;
  description: string;
  content: string;
  url: string;
  imageUrl: string;
  source: string;
  publishedAt: string;
  author: string;
}

export interface NewsResponse {
  status: string;
  totalResults: number;
  articles: NewsArticle[];
  page?: number;
  pageSize?: number;
  message?: string;
}

// Get API base URL dynamically based on current hostname
// This function is called at runtime, not build time
const getApiBaseUrl = (): string => {
  // Runtime check FIRST: if we're on production domain, use relative URL
  // This must be checked before process.env to override build-time values
  try {
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
      const hostname = window.location.hostname;
      // Production domains - use relative URL (empty string = same origin)
      if (hostname === 'outchou.com' || hostname === 'www.outchou.com' || hostname === '82.165.93.19') {
        return ''; // Relative URL for production
      }
    }
  } catch (e) {
    // Fallback if window is not available
  }
  
  // Check environment variable (for explicit override in development)
  // Only used if not in production
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Development - use localhost (only if not in production)
  return 'http://localhost:5001';
};

export const newsService = {
  /**
   * Fetch Algeria-related news articles from custom cultural sources (.dz, .com, .tv, .org)
   */
  async getAlgeriaNews(pageSize: number = 10, page: number = 1): Promise<NewsResponse> {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/news/cultural-sources?pageSize=${pageSize}`
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching Algeria news:', error);
      throw error;
    }
  },

  /**
   * Fetch Algeria headlines from custom cultural sources
   */
  async getAlgeriaHeadlines(pageSize: number = 5): Promise<NewsResponse> {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/news/cultural-sources?pageSize=${pageSize}`
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching Algeria headlines:', error);
      throw error;
    }
  },

  /**
   * Format date for display (date only, no time)
   */
  formatDate(dateString: string): string {
    try {
      // Si c'est déjà un format lisible en français, le retourner tel quel
      if (dateString && /^\d{1,2}\s+\w+\s+\d{4}$/i.test(dateString.trim())) {
        return dateString.trim();
      }
      
      // Essayer de parser la date
      let date = new Date(dateString);
      
      // Si la date n'est pas valide, essayer d'autres formats
      if (isNaN(date.getTime())) {
        // Format DD/MM/YYYY
        const parts = dateString.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
        if (parts) {
          date = new Date(parseInt(parts[3]), parseInt(parts[2]) - 1, parseInt(parts[1]));
        }
      }
      
      // Si toujours pas valide, retourner la chaîne originale
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      // Convert to CET timezone
      const cetDate = new Date(date.toLocaleString("en-US", {timeZone: "Europe/Paris"}));
      
      return cetDate.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  },

  /**
   * Truncate text to specified length
   */
  truncateText(text: string, maxLength: number = 150): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  },

  /**
   * Check if backend is available
   */
  async checkBackendHealth(): Promise<boolean> {
    try {
      const apiBaseUrl = getApiBaseUrl();
      console.log('[newsService] API Base URL:', apiBaseUrl, 'Hostname:', typeof window !== 'undefined' ? window.location.hostname : 'N/A');
      const response = await fetch(`${apiBaseUrl}/api/health`);
      return response.ok;
    } catch (error) {
      console.error('Backend health check failed:', error);
      return false;
    }
  }
}; 
